import { useEffect, useState } from 'react';
import { Card, Select, Spin, Table, Tag, Typography, Progress, Space } from 'antd';
import { avmApi } from '../../services/api';

const { Text } = Typography;

const EVENT_LABELS: Record<string, string> = {
  start:        '🟢 Abrió el formulario',
  address:      '📍 Rellenó dirección',
  category:     '🏠 Seleccionó categoría',
  family:       '🔽 Seleccionó familia/subtipo',
  size:         '📐 Rellenó superficie',
  zone:         '📌 Seleccionó zona',
  neighborhood: '🏘️ Seleccionó barrio',
  step0_done:   '✅ Completó Step 1 (datos básicos)',
  step1_done:   '✅ Completó Step 2 (detalles)',
  otp_sent:     '📧 Solicitó código OTP',
  completed:    '🎉 Verificó y recibió valoración',
};

const FUNNEL_ORDER = [
  'start', 'address', 'category', 'family', 'size',
  'zone', 'neighborhood', 'step0_done', 'step1_done', 'otp_sent', 'completed',
];

interface FunnelRow { event: string; sessions: number; }
interface SessionRow {
  sessionId: string;
  event: string;
  propCategory?: string | null;
  zone?: string | null;
  createdAt: string;
}
interface CategoryRow { propCategory: string | null; count: number; }

export default function AvmSessionsTab() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [funnel, setFunnel] = useState<FunnelRow[]>([]);
  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [byCategory, setByCategory] = useState<CategoryRow[]>([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await avmApi.getValoradorSessions(days);
      setFunnel(data.funnel || []);
      setSessions(data.sessions || []);
      setByCategory(data.byCategory || []);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [days]);

  const startCount = funnel.find((f) => f.event === 'start')?.sessions || 1;

  const funnelRows = FUNNEL_ORDER.map((event) => {
    const found = funnel.find((f) => f.event === event);
    const sessions = found?.sessions || 0;
    const pct = startCount > 0 ? Math.round((sessions / startCount) * 100) : 0;
    return { event, sessions, pct };
  });

  const sessionColumns = [
    {
      title: 'Session ID',
      dataIndex: 'sessionId',
      key: 'sessionId',
      width: 120,
      render: (v: string) => <Text type="secondary" style={{ fontSize: 11 }}>{v.slice(0, 8)}…</Text>,
    },
    {
      title: 'Último evento',
      dataIndex: 'event',
      key: 'event',
      render: (v: string) => (
        <Tag color={v === 'completed' ? 'green' : v === 'otp_sent' ? 'blue' : v === 'step0_done' ? 'gold' : 'default'}>
          {EVENT_LABELS[v] || v}
        </Tag>
      ),
    },
    {
      title: 'Categoría',
      dataIndex: 'propCategory',
      key: 'propCategory',
      render: (v?: string | null) => v || '—',
    },
    {
      title: 'Zona',
      dataIndex: 'zone',
      key: 'zone',
      render: (v?: string | null) => v || '—',
    },
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v: string) => new Date(v).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }),
    },
  ];

  return (
    <Spin spinning={loading}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Text strong>Actividad del formulario valorador</Text>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>Período:</Text>
          <Select
            value={days}
            onChange={(v) => setDays(v)}
            options={[
              { value: 7, label: 'Últimos 7 días' },
              { value: 30, label: 'Últimos 30 días' },
              { value: 90, label: 'Últimos 90 días' },
            ]}
            style={{ width: 160 }}
          />
        </Space>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        {/* Funnel */}
        <Card title="Embudo del formulario (Step 1)" size="small">
          {funnelRows.map(({ event, sessions: count, pct }) => (
            <div key={event} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                <Text style={{ fontSize: 12 }}>{EVENT_LABELS[event] || event}</Text>
                <Text type="secondary" style={{ fontSize: 12 }}>{count} sesiones ({pct}%)</Text>
              </div>
              <Progress
                percent={pct}
                showInfo={false}
                strokeColor={
                  event === 'completed' ? '#52c41a' :
                  event === 'otp_sent' ? '#1677ff' :
                  event === 'step0_done' ? '#faad14' :
                  '#b9a9a5'
                }
                size="small"
              />
            </div>
          ))}
        </Card>

        {/* Category breakdown */}
        <Card title="Distribución por categoría de propiedad" size="small">
          {byCategory.length === 0 ? (
            <Text type="secondary">Sin datos</Text>
          ) : (
            byCategory.map(({ propCategory, count }) => {
              const total = byCategory.reduce((s, r) => s + r.count, 0);
              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
              return (
                <div key={propCategory || '__null__'} style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                    <Text style={{ fontSize: 12 }}>{propCategory || '(sin seleccionar)'}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{count} ({pct}%)</Text>
                  </div>
                  <Progress percent={pct} showInfo={false} strokeColor="#b9a9a5" size="small" />
                </div>
              );
            })
          )}
        </Card>
      </div>

      {/* Recent sessions */}
      <Card title="Últimas 100 sesiones" size="small">
        <Table
          rowKey="sessionId"
          columns={sessionColumns}
          dataSource={sessions}
          pagination={{ pageSize: 20 }}
          size="small"
        />
      </Card>
    </Spin>
  );
}
